import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getInvoiceDetails,
  getSubscriptionDetails,
  cancelMySubscription,
} from "../../../services/clientWebsite";
import { updatePurchasedSubscription } from "../../../redux/slices/authSlice";
import { notify } from "../../../components/Toast";

const InvoiceSection = () => {
  // Local state for invoices and loading status
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [userSubscriptions, setUserSubscriptions] = useState([]);

  // Get logged-in user and subscription details from Redux store
  const user = useSelector((state) => state.auth.user);
  const activeSubscription = useSelector(
    (state) => state.auth.activeSubscription
  );
  const isRenewalSubscription = useSelector(
    (state) => state.auth.is_renewal_subscription
  );

  const dispatch = useDispatch();

  // Function to clean/normalize invoice data coming from API
  const normalizeInvoiceData = (invoiceList) => {
console.log("Normalizing invoices:", invoiceList);

    return invoiceList.map((invoice, index) => ({
      serial_number: index + 1,
      id: invoice.id || Math.random().toString(36).substr(2, 9),
      invoice_number:
        invoice.invoice_number || invoice.invoice_id || `INV-${Date.now()}`,
      purchase_date:
        invoice.purchase_date ||
        invoice.date ||
        new Date().toISOString().split("T")[0],
      // map due date: prefer explicit due_date, then subscription_end_date (or nested user_subscription.subscription_end_date), then other common names
      due_date:
        invoice.due_date ||
        invoice.subscription_end_date ||
        (invoice.user_subscription && invoice.user_subscription.subscription_end_date) ||
        invoice.dueDate ||
        invoice.due ||
        null,
      plan_name:
        invoice.plan_name ||
        invoice.product_name ||
        invoice.title ||
        "Unknown Plan",
      amount_paid: parseFloat(
        invoice.amount_paid || invoice.amount || invoice.price || 0
      ),
      payment_method: invoice.payment_method || invoice.method || "Unknown",
      status: (invoice.status || invoice.payment_status || "PAID").toString(),
      duration: invoice.duration || invoice.period || "1",
      description:
        invoice.description || invoice.desc || "Fitness plan subscription",
      name: invoice.name || invoice.customer_name || user?.name || "Customer",
      email:
        invoice.email ||
        invoice.customer_email ||
        user?.email ||
        "customer@example.com",
      phone: invoice.phone || invoice.customer_phone || user?.phone || "N/A",
      stripe_payment_id:
        invoice.stripe_payment_id ||
        invoice.transaction_id ||
        invoice.payment_id ||
        `TXN-${Date.now()}`,
      invoice_url: invoice.invoice_url,
      // Add subscription-related fields for status determination
      subscription_id: invoice.subscription_id || invoice.plan_id,
      subscription_status: invoice.subscription_status || invoice.user_subscription?.subscription_status,
      is_renewable: invoice.is_renewable || invoice.user_subscription?.is_renewable,
    }));
  };

  // Fetch invoices for the logged-in user
  const handleFetchInvoices = async () => {
    setLoading(true);
    try {
      const resp = await getInvoiceDetails(user.id);

      if (resp?.invoices?.length) {
        setInvoices(normalizeInvoiceData(resp.invoices));
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Example of previous code for downloading invoice (currently not used)
  /*
  const handleDownloadInvoice = async (invoice) => {
    try {
      const data = await request.get(`/invoice/${user?.id}/${invoice.invoice_number}`);
      if (data?.success && data?.invoice_url) {
        window.open(data.invoice_url, "_blank"); // Open PDF in new tab
      } else {
        notify.error("Invoice URL not found");
      }
    } catch (error) {
      console.error("Download error:", error);
      notify.error("Error downloading invoice");
    }
  };
  */

  // Fetch invoices when user is available and has active subscription
  useEffect(() => {
    if (user?.id && activeSubscription) {
      handleFetchInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeSubscription]);

  // Fetch subscription data when invoices are loaded to ensure status comparison works
  useEffect(() => {
    if (invoices.length > 0 && userSubscriptions.length === 0) {
      handleFetchUserSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices.length]);

  // Fetch user subscription details and update Redux store
  const handleFetchUserSubscription = async () => {
    try {
      const response = await getSubscriptionDetails();
      if (response.success) {
        const subscriptionData = response.data || [];
        setUserSubscriptions(subscriptionData);
        
        // Extract subscription details for Redux store
        let subscriptionIds = [];
        let chat_enable = null;
        let hasDocument = null;
        let is_renewable_subscription = null;
        
        for (const dataItem of subscriptionData) {
          // Collect all subscription IDs
          if (dataItem?.subscription_details?.subscription_id) {
            subscriptionIds.push(dataItem.subscription_details.subscription_id);
          }
          
          // Get chat enable if not found yet (from any subscription)
          if (chat_enable === null && dataItem?.user_subscription?.chat_enable !== undefined) {
            chat_enable = dataItem.user_subscription.chat_enable;
          }
          
          // Get has document if not found yet (from any subscription)
          if (hasDocument === null && dataItem?.subscription_content?.has_document !== undefined) {
            hasDocument = dataItem.subscription_content.has_document;
          }
          // Get is_renewable - set to true if ANY subscription is renewable
          if (dataItem?.subscription_details?.is_renewable !== undefined) {
            // Convert to boolean and check if it's true
            const isRenewable = Boolean(dataItem.subscription_details.is_renewable);
            if (isRenewable) {
              is_renewable_subscription = true;
            } else if (is_renewable_subscription === null) {
              // Only set to false if we haven't found any renewable subscription yet
              is_renewable_subscription = false;
            }
          }
        }

        const primarySubscriptionId = subscriptionIds.length > 0 ? subscriptionIds : null;

        dispatch(
          updatePurchasedSubscription({
            purchasedSubscription: primarySubscriptionId,
            chatEnabled: chat_enable,
            activeSubscription: response?.has_active_subscription,
            subscription_has_document: hasDocument,
            is_renewal_subscription: is_renewable_subscription,
          })
        );
      }
    } catch (error) {
      console.error("error", error);
    }
  };

  // Run once when component mounts to fetch subscription details
  useEffect(() => {
    handleFetchUserSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler for canceling subscription
  const handleCancelSubscription = async () => {
    // More flexible detection for renewable subscriptions - tries multiple approaches
    const isRenewableSubscription = (sub) => {
      // Check explicit renewable flag
      if (sub.subscription_details?.is_renewable === 1 || 
          sub.subscription_details?.is_renewable === true ||
          sub.subscription_details?.is_renewable === "1" ||
          sub.subscription_details?.is_renewable === "true") {
        return true;
      }
      
      // Check duration strings for monthly/weekly indicators
      const duration = sub.subscription_details?.duration || sub.duration;
      if (duration && typeof duration === 'string' && 
          (duration.toLowerCase().includes('month') || 
           duration.toLowerCase().includes('week') || 
           duration.toLowerCase().includes('day'))) {
        return true;
      }
      
      // Check for billing period or recurring flag
      if (sub.subscription_details?.billing_period || 
          sub.billing_period ||
          sub.subscription_details?.recurring === true ||
          sub.recurring === true) {
        return true;
      }
      
      // Check plan name for monthly/recurring indicators
      const planName = sub.subscription_details?.subscription_name || 
                     sub.plan_name || 
                     sub.subscription_name;
      if (planName && typeof planName === 'string' && 
          (planName.toLowerCase().includes('month') || 
           planName.toLowerCase().includes('recur'))) {
        return true;
      }
      
      return false;
    };
    
    // Count subscriptions using our more flexible detection
    const renewableCount = userSubscriptions.filter(isRenewableSubscription).length;
    const nonRenewableCount = userSubscriptions.length - renewableCount;
    
    // Fallback - if we can't detect any renewable subscriptions but have active subscriptions,
    // assume at least one is renewable for better user experience
    const assumedRenewableCount = renewableCount === 0 && userSubscriptions.length > 0 ? 1 : renewableCount;
    const assumedNonRenewableCount = renewableCount === 0 && userSubscriptions.length > 0 ? 
                                  userSubscriptions.length - 1 : nonRenewableCount;
    
    let confirmMessage = "Are you sure you want to cancel your subscription? This action cannot be undone.";
    
    if (assumedRenewableCount > 0 && assumedNonRenewableCount > 0) {
      confirmMessage = `You have ${assumedRenewableCount} renewable and ${assumedNonRenewableCount} non-renewable subscription(s). Only renewable subscriptions will be cancelled. Continue?`;
    } else if (assumedRenewableCount > 1) {
      confirmMessage = `You have ${assumedRenewableCount} renewable subscriptions. All renewable subscriptions will be cancelled. Continue?`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCancelLoading(true);
    try {
      // Use the main cancel API but handle the response appropriately
      const response = await cancelMySubscription();
      
      // Consider both success and different formats of success responses
      const isSuccessful = response.success || 
                         response.status === 'success' || 
                         response.status === 200 || 
                         (response.data && response.data.success);
      
      if (isSuccessful) {
        let successMessage = response.message || "Your subscription has been cancelled successfully";
        
        // Customize message based on subscription mix
        if (renewableCount > 0 && nonRenewableCount > 0) {
          successMessage = "Your renewable subscription(s) have been cancelled successfully. Non-renewable subscriptions remain active.";
        }
        
        notify.success(successMessage);
        
        // Only update Redux state if all subscriptions are cancelled
        if (nonRenewableCount === 0) {
          dispatch(
            updatePurchasedSubscription({
              purchasedSubscription: null,
              chatEnabled: false,
              activeSubscription: false,
            })
          );
        }
        
        // Refresh subscription and invoice data
        await handleFetchUserSubscription();
        await handleFetchInvoices();
      } else {
        
        // Handle specific error for non-renewable subscriptions
        const errorMessage = response.message || 
                           (response.data && response.data.message) || 
                           response.error || 
                           "Unknown error";
        
        if (errorMessage.toLowerCase().includes("one-time") || 
            errorMessage.toLowerCase().includes("renewable")) {
          notify.error("Only renewable subscriptions can be cancelled. Your non-renewable (one-time purchase) subscriptions cannot be cancelled through this method.");
        } else {
          notify.error(`Cancellation failed: ${errorMessage || "Please try again later"}`);
        }
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      
      // Handle all possible error formats and paths
      let errorMessage = "";
      
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.message) {
        errorMessage = error.response.message;
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else {
        errorMessage = "Unknown error occurred";
      }
      
      console.log("Cancellation error details:", errorMessage);
      
      // Handle specific error message for non-renewable subscriptions
      if (errorMessage.toLowerCase().includes("one-time") || 
          errorMessage.toLowerCase().includes("renewable") ||
          errorMessage.toLowerCase().includes("cannot cancel")) {
        notify.error("Only renewable subscriptions can be cancelled. Your non-renewable (one-time purchase) subscriptions cannot be cancelled through this method.");
      } else {
        // More helpful error message that includes the server's error
        notify.error(`Error cancelling subscription: ${errorMessage}`);
      }
    } finally {
      setCancelLoading(false);
      
      // Regardless of success/failure, always refresh data to show current state
      handleFetchUserSubscription();
      handleFetchInvoices();
    }
  };

  // Function to determine actual subscription status - More conservative approach
  const getActualSubscriptionStatus = (invoice) => {
    console.log('Determining status for invoice:', invoice.invoice_number, {
      subscription_id: invoice.subscription_id,
      subscription_status: invoice.subscription_status,
      due_date: invoice.due_date,
      duration: invoice.duration,
      status: invoice.status
    });

    // First check if the subscription is explicitly cancelled or inactive in the invoice
    if (invoice.subscription_status) {
      const subStatus = invoice.subscription_status.toLowerCase();
      if (subStatus === 'cancelled' || subStatus === 'canceled' || subStatus === 'inactive') {
        console.log('Status: CANCELLED (explicit status)');
        return 'CANCELLED';
      }
    }

    // Check expiration first based on due date
    let dueDateObj = null;
    if (invoice.duration && invoice.duration.toLowerCase().replace(/\s/g, '') === "lifetime") {
      console.log('Status: ACTIVE (lifetime plan)');
      return 'ACTIVE'; // lifetime plans don't expire
    } else if (invoice.due_date) {
      dueDateObj = new Date(invoice.due_date);
    } else if (invoice.purchase_date && invoice.duration) {
      const pd = new Date(invoice.purchase_date);
      if (!isNaN(pd.getTime())) {
        dueDateObj = new Date(pd);
        const weeks = parseInt(invoice.duration, 10) || 4;
        dueDateObj.setDate(dueDateObj.getDate() + weeks * 7);
      }
    }

    const now = new Date();
    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);
    
    // Check if expired based on date
    if (dueDateObj && dueDateObj < endOfToday) {
      console.log('Status: EXPIRED (past due date)');
      return 'EXPIRED';
    }

    // If due date is in the future, it should be active
    if (dueDateObj && dueDateObj >= endOfToday) {
      console.log('Status: ACTIVE (future due date)');
      return 'ACTIVE';
    }

    // Check if subscription exists in current user subscriptions
    const currentSubscription = userSubscriptions.find(sub => 
      sub.subscription_details?.subscription_id === invoice.subscription_id ||
      sub.subscription_details?.id === invoice.subscription_id ||
      sub.id === invoice.subscription_id
    );

    if (currentSubscription) {
      // Check the subscription status from the current subscriptions
      const userSubStatus = currentSubscription.user_subscription?.subscription_status;
      if (userSubStatus) {
        const status = userSubStatus.toLowerCase();
        if (status === 'cancelled' || status === 'canceled' || status === 'inactive') {
          console.log('Status: CANCELLED (user subscription status)');
          return 'CANCELLED';
        }
      }
      console.log('Status: ACTIVE (found in subscriptions)');
      return 'ACTIVE';
    }

    // Default to active for paid invoices - be conservative
    const rawStatus = (invoice.status || "").toUpperCase();
    const finalStatus = rawStatus === 'PAID' ? 'ACTIVE' : (rawStatus || 'ACTIVE');
    console.log('Status:', finalStatus, '(default/conservative)');
    return finalStatus;
  };

  // Utility: Get status icon based on payment status
  const getStatusIcon = (status) => {
    switch ((status || "").toUpperCase()) {
      case "PAID":
      case "ACTIVE":
        return "✅";
      case "PENDING":
        return "⏳";
      case "FAILED":
        return "❌";
      case "EXPIRED":
        return "⚠️";
      case "CANCELLED":
      case "CANCELED":
        return "🚫";
      default:
        return "✅";
    }
  };

  // Utility: Get readable status text
  const getStatusText = (status) => {
    switch ((status || "").toUpperCase()) {
      case "PAID":
      case "ACTIVE":
        return "Active";
      case "PENDING":
        return "Pending";
      case "FAILED":
        return "Failed";
      case "EXPIRED":
        return "Expired";
      case "CANCELLED":
      case "CANCELED":
        return "Cancelled";
      default:
        return status || "Active";
    }
  };

  // helper to format dates safely
  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Show loading spinner while fetching invoices
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading invoices...</p>
      </div>
    );
  }

  // (openInvoice not used; use inline window.open when needed)

  return (
    <div className="invoice-section">
      
      {/* Cancel Subscription Button - ALWAYS show if there's an active subscription */}
      {activeSubscription && isRenewalSubscription && (
        <div style={{ marginBottom: "16px", textAlign: "right" }}>
          <button
            className="btn-cancel-subscription"
            style={{
              background: cancelLoading ? "#95a5a6" : "#e74c3c",
              color: "#fff",
              padding: "8px 16px",
              border: "none",
              borderRadius: "4px",
              cursor: cancelLoading ? "not-allowed" : "pointer",
            }}
            onClick={handleCancelSubscription}
            disabled={cancelLoading}
          >
            {cancelLoading ? "Cancelling..." : "Cancel Subscription"}
          </button>
        </div>
      )}
      <div className="invoices-table-container">
        {invoices.length === 0 ? (
          // Case: No invoices found
          <div className="no-invoices">
            <div className="no-invoices-icon">📄</div>
            <h3>No Invoices Found</h3>
            <p>You haven't made any purchases yet.</p>
          </div>
        ) : (
          // Case: Show invoice table
          <div className="invoices-table-01">
            <table className="modern-table-01">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th>Due Date</th>
                  <th>Plan</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  // Get the actual subscription status using our enhanced logic
                  const displayStatus = getActualSubscriptionStatus(invoice);

                  // determine due date for display purposes
                  let dueDateObj = null;
                  if (invoice.duration && invoice.duration.toLowerCase().replace(/\s/g, '') === "lifetime") {
                    dueDateObj = null; // lifetime plans don't have a due date
                  } else if (invoice.due_date) {
                    dueDateObj = new Date(invoice.due_date);
                  } else if (invoice.purchase_date && invoice.duration) {
                    const pd = new Date(invoice.purchase_date);
                    if (!isNaN(pd.getTime())) {
                      dueDateObj = new Date(pd);
                      const weeks = parseInt(invoice.duration, 10) || 4; // default to 4 weeks if duration is 0 or invalid
                      dueDateObj.setDate(dueDateObj.getDate() + weeks * 7);
                    }
                  }

                  return (
                    <tr key={invoice.id}>
                      <td>
                        <span className="serial-number">
                          {invoice.serial_number}
                        </span>
                      </td>
                      <td>
                        <span className="invoice-number">
                          {invoice.invoice_number}
                        </span>
                      </td>
                      <td>
                        <span className="invoice-date">
                          {formatDate(invoice.purchase_date)}
                        </span>
                      </td>

                      <td>
                        <span className="invoice-due-date">
                          {invoice.duration && invoice.duration.toLowerCase().replace(/\s/g, '') === "lifetime"
                            ? "—"
                            : dueDateObj
                            ? dueDateObj.toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : "—"}
                        </span>
                      </td>

                      <td>
                        <div className="plan-info">
                          <span className="plan-name">{invoice.plan_name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="plan-duration">
                          {invoice.duration}
                        </span>
                      </td>
                      <td>
                        <div
                          className={`status-badge status-${displayStatus.toLowerCase()}`}
                        >
                          <span className="status-icon">
                            {getStatusIcon(displayStatus)}
                          </span>
                          <span className="status-text">
                            {getStatusText(displayStatus)}
                          </span>
                        </div>
                      </td>
                      {console.log("shdgcbuisjd", invoice)}
                      <td>
                        <span className="amount">
                          ${
                            typeof invoice.amount_paid === "number"
                              ? invoice.amount_paid.toFixed(2)
                              : parseFloat(invoice.amount_paid || 0).toFixed(2)
                          }
                        </span>
                      </td>
                      <td>
                        {/* Download / View Invoice Button */}
                        <div className="action-buttons">
                          <button
                            className="btn-download"
                            title="Download PDF"
                            onClick={() =>
                              window.open(invoice.invoice_url, "_blank")
                            }
                          >
                            {/* Download Icon */}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            >
                              <g fill="none">
                                <path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z" />
                                <path
                                  fill="#333333"
                                  d="M8 5v2H5v13h14V7h-3V5h3a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zm4-3a1 1 0 0 1 1 1v10.828L14.828 12a1 1 0 1 1 1.415 1.414l-3.36 3.359a1.25 1.25 0 0 1-1.767 0l-3.359-3.359A1 1 0 1 1 9.172 12L11 13.828V3a1 1 0 0 1 1-1"
                                />
                              </g>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceSection;
